import strings from '../../../strings/strings.json';
import { ButtonRowRight } from '../../../components/Elements';
import { logError } from '../../../lib/debug-helpers';
import { createErrorMessage, fieldErrorCodes, requiredFieldErrorMessage } from '../../../shared/error-messages';
import TextField from '../../../components/forms/TextField';
import { useNavigate } from 'react-router-dom';
import useModifyRequest from '../../../hooks/useModifyRequest';
import useNeveForm from 'components/forms/NeveForm';
import { FormCard } from '../../../components/forms/FormComponents';
import { capitalsRegex } from '../../../components/accessibilityGuide/utils';
import AccessibilityNotifier from '../../../components/notifiers/AccessibilityNotifier';
import { styled } from 'styled-components';
import Hint from 'components/Hint';
import Button from 'components/buttons/Button';

export type CreateCourseInput = {
    courseTitle: string;
};

export default function CreateCourseForm(): JSX.Element {
    const navigate = useNavigate();
    const { modifyData: createCourse } = useModifyRequest('courses/', 'POST');

    const {
        register,
        watch,
        handleSubmit,
        formState: { errors },
    } = useNeveForm<CreateCourseInput>();

    async function onSubmit(data: CreateCourseInput) {
        const response = await createCourse({
            title: data.courseTitle,
        });

        const { value: courseId, errors } = response;
        if (errors) {
            logError(errors);
            return;
        }

        if (courseId) {
            navigate(`/build-course/${courseId}/`, { replace: true });
        }
    }

    const courseTitle = watch('courseTitle');
    const courseTitleMaxLength = 120;

    return (
        <>
            <Hint hint={strings.createCourseModal.titleHint} />
            <Form onSubmit={handleSubmit(onSubmit)}>
                <TextField
                    maxLength={courseTitleMaxLength}
                    fieldName="courseTitle"
                    labelText={strings.createCoursePage.courseNameInputLabel}
                    inputAria={strings.createCoursePage.courseNameInputLabelAria}
                    errorMessage={errors.courseTitle?.message}
                    required
                    autoFocus
                    {...register('courseTitle', {
                        required: {
                            value: true,
                            message: requiredFieldErrorMessage(strings.createCoursePage.courseNameInputLabel),
                        },
                        maxLength: {
                            value: courseTitleMaxLength,
                            message: createErrorMessage(
                                strings.createCoursePage.courseNameInputLabel,
                                fieldErrorCodes.maxLength,
                            ),
                        },
                    })}
                />
                {capitalsRegex.test(courseTitle) && <AccessibilityNotifier accessibilityGuideKey={'textAllCaps'} />}
                <ButtonRowRight>
                    <Button
                        type="submit"
                        title={strings.createCoursePage.createButton}
                        aria={strings.createCoursePage.createButtonAriaLabel}
                    />
                </ButtonRowRight>
            </Form>
        </>
    );
}

export const Form = styled(FormCard)`
    border: unset;
    box-shadow: unset;
    padding: unset;
    width: 100%;
`;
